import React from "react";
import AuthLayout from "./components/AuthLayout";
import EmailEntry from "./routes/EmailEntry";
import EmailVerification from "./routes/EmailVerification";
import MethodSelection from "./routes/MethodSelection";
import PasswordEntry from "./routes/PasswordEntry";
import CompanyChooser from "./routes/CompanyChooser";
import PortalChooser from "./routes/PortalChooser";
import ChainSearch from "./routes/ChainSearch";
import SetPassword from "./routes/SetPassword";
import ForgotPassword from "./routes/ForgotPassword";
import ResetSent from "./routes/ResetSent";
import TokenError from "./routes/TokenError";
import DeviceApproval from "./routes/DeviceApproval";
import { useAuthFlow } from "./hooks/useAuthFlow";
import { getRecents } from "./lib/recentChains";
import ResumeCard from "./components/ResumeCard";

// Above this many portals, the chooser shows a client-side filter + scroll
// region rather than an overflowing static list. Normal users span 1-2
// portals; support/master identities can span dozens. ZC-5705.
const PORTAL_FILTER_THRESHOLD = 8;

export default function LoginApp() {
  const config = (window as Record<string, unknown>).__AUTH_CONFIG__ as
    | Record<string, unknown>
    | undefined;
  const turnstileSiteKey = (config?.turnstile_site_key as string) ?? "";
  // Forward the encrypted OAuth authorize payload (when this bundle is
  // reached via /oauth/authorize → /auth/login) so authenticate /
  // selectCompany / acceptInvite can complete league's flow at their
  // terminals. Empty string means "legacy entry" — those API calls behave
  // exactly as they did before Phase B/C.
  const authRequest = (config?.auth_request as string) ?? "";
  // Portal login callback parameters threaded through the login SPA so
  // authenticate / selectCompany / acceptInvite can forward them (ZC-3444).
  const returnTo = (config?.return_to as string) ?? "";
  const callbackUrl = (config?.callback_url as string) ?? "";

  const {
    state,
    handleEmailSubmit,
    handleMethodSelect,
    handleVerificationSubmit,
    handlePasswordSubmit,
    handleCompanySelect,
    handlePortalSelect,
    handleChainSearch,
    handleDeviceApprove,
    handleDeviceDeny,
    handleResendCode,
    handleSetPassword,
    handleForgotPassword,
    handleSendReset,
    handleExpiredInviteReset,
    goBack,
    handleBackToSignIn,
    handleResumeContinue,
    handleSwitchUser,
    handleLogout,
  } = useAuthFlow(authRequest, returnTo, callbackUrl);

  // ZC-5707: When the user resumed via the chooser screens, show the identity
  // chip so they can log out or switch to a different account.
  const resumeIdentity = state.resumeLabel
    ? {
        label: state.resumeLabel,
        onLogout: handleLogout,
        onSwitchUser: handleSwitchUser,
      }
    : undefined;

  const renderScreen = () => {
    switch (state.step) {
      case "email":
        return (
          <>
            <EmailEntry
              onSubmit={handleEmailSubmit}
              loading={state.loading}
              error={state.error}
              turnstileSiteKey={turnstileSiteKey}
              successMessage={
                state.passwordResetSuccess
                  ? "Your password has been set. Sign in to continue."
                  : state.loggedOut
                  ? "You've been signed out."
                  : undefined
              }
            />
            {state.resumable && state.resumeLabel && (
              <ResumeCard
                label={state.resumeLabel}
                onContinue={handleResumeContinue}
                onForget={handleSwitchUser}
                loading={state.loading}
              />
            )}
          </>
        );

      case "verification":
        return (
          <EmailVerification
            email={state.email}
            onSubmit={handleVerificationSubmit}
            onResend={handleResendCode}
            onBack={goBack}
            loading={state.loading}
            error={state.error}
          />
        );

      case "method-selection":
        return (
          <MethodSelection
            authMethods={state.authMethods}
            onSelect={handleMethodSelect}
            onBack={goBack}
            loading={state.loading}
            error={state.error}
          />
        );

      case "password":
        return (
          <PasswordEntry
            email={state.email}
            onSubmit={handlePasswordSubmit}
            onForgotPassword={handleForgotPassword}
            onBack={goBack}
            loading={state.loading}
            error={state.error}
          />
        );

      case "portal-chooser":
        return (
          <PortalChooser
            portals={state.portals ?? []}
            onSelect={handlePortalSelect}
            onBack={goBack}
            loading={state.loading}
            error={state.error}
            // Only surface the filter/scroll for large lists (support/master
            // users span many portals). A normal 2-portal user keeps the plain
            // chooser. ZC-5705.
            filterable={(state.portals?.length ?? 0) > PORTAL_FILTER_THRESHOLD}
            identity={resumeIdentity}
          />
        );

      case "company-chooser":
        return (
          <CompanyChooser
            companies={state.filteredCompanies ?? state.companies ?? []}
            onSelect={handleCompanySelect}
            onBack={goBack}
            loading={state.loading}
            error={state.error}
            identity={resumeIdentity}
          />
        );

      case "chain-search":
        return (
          <ChainSearch
            results={state.searchResults}
            searchLoading={state.searchLoading}
            // ZC-5733: recent selections for this identity, mapped to the Company
            // shape so they render with the same card + selection path.
            recents={(state.identityId ? getRecents(state.identityId) : []).map(
              (r) => ({
                portal_uid: r.portal_uid,
                chain_code: r.chain_code,
                company_name: r.name,
                portal_user_public_id: null,
                requires_provisioning: false,
                auto_provisioning_enabled: false,
                is_support: true,
              })
            )}
            onSearch={handleChainSearch}
            onSelect={handleCompanySelect}
            onBack={goBack}
            loading={state.loading}
            error={state.error}
          />
        );

      case "invite-loading":
      case "sso-loading":
        return (
          <div className="text-center py-5">
            <span
              className="spinner-border text-primary"
              role="status"
              aria-live="polite"
            >
              <span className="visually-hidden">Validating...</span>
            </span>
          </div>
        );

      case "set-password":
        return (
          <SetPassword
            email={state.email}
            companyName={state.companyName}
            tokenType={state.tokenType}
            onSubmit={handleSetPassword}
            loading={state.loading}
            error={state.error}
          />
        );

      case "forgot-password":
        return (
          <ForgotPassword
            email={state.email}
            onSubmit={handleSendReset}
            onBack={goBack}
            loading={state.loading}
            error={state.error}
          />
        );

      case "reset-sent":
        return (
          <ResetSent email={state.email} onBackToSignIn={handleBackToSignIn} />
        );

      case "token-error":
        return (
          <TokenError
            expired={state.tokenExpired}
            onReset={handleExpiredInviteReset}
            onBackToSignIn={handleBackToSignIn}
            loading={state.loading}
            error={state.error}
          />
        );

      case "device-consent":
        return (
          <DeviceApproval
            clientName={state.deviceClientName ?? ""}
            scopes={state.deviceScopes}
            onApprove={handleDeviceApprove}
            onDeny={handleDeviceDeny}
            loading={state.loading}
            error={state.error}
          />
        );

      case "device-approved":
        return (
          <div className="text-center py-5">
            <h2 className="h3 fw-bold text-dark mb-3">Device Approved</h2>
            <p className="text-muted">
              You can return to your device to complete the authorization.
            </p>
          </div>
        );

      case "device-denied":
        return (
          <div className="text-center py-5">
            <h2 className="h3 fw-bold text-dark mb-3">Device Access Denied</h2>
            <p className="text-muted">
              You denied authorization for this device.
            </p>
          </div>
        );
    }
  };

  return <AuthLayout>{renderScreen()}</AuthLayout>;
}
